import type { Assign } from '@ark-ui/react'
import { Field as ArkField } from '@ark-ui/react/field'
import { forwardRef } from 'react'
import { cn } from '~/utils/cn'
import type { InputHTMLAttributes } from 'react'
import type { InputTextSharedProps } from './input-text-root'

export type InputTextFieldProps = Assign<
  React.ComponentProps<typeof ArkField.Input>,
  InputTextSharedProps & {
    readOnly?: boolean
    isActive?: boolean
    setActive?: (value: boolean) => void
  }
> &
  Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'size' | 'disabled' | 'readOnly' | 'className'
  >

export const InputTextField = forwardRef<HTMLInputElement, InputTextFieldProps>(
  (
    {
      onFocus,
      onBlur,
      size = 'ml',
      disabled,
      readOnly,
      isActive,
      setActive,
      ...props
    },
    ref
  ) => {
    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setActive?.(true)
      onFocus?.(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setActive?.(false)
      onBlur?.(event)
    }

    return (
      <ArkField.Input
        ref={ref}
        disabled={disabled}
        readOnly={readOnly}
        placeholder="Placeholder is hidden"
        className={cn(
          'peer/field flex w-[100%] bg-[transparent] !font-rdmodern font-regular text-text-neutral-alternative tracking-tiny leading-small placeholder:opacity-transparent focus-visible:border-none focus-visible:outline-none',
          {
            'text-threepulse': size === 'md',
            'text-threeandhalfpulse':
              size === 'ml' || size === 'lg' || size === 'xl',
            'text-text-neutral-readonly': readOnly,
            'cursor-not-allowed text-text-neutral-disabled': disabled,
            'text-text-neutral': isActive,
          }
        )}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />
    )
  }
)

InputTextField.displayName = 'InputTextField'
